import moment from "moment";
import { AIMEVals, exemptionAmounts, FRAValues, widowValues, YOCValues } from "./CalculationData";

export const formatter = new Intl.NumberFormat('en', { style: 'decimal', currency: 'USD', maximumFractionDigits: 0 });

class SocialSecurityCalculator {
    constructor(userType, answers) {
        let oppositeType = (userType === 'main' ? 'spouse' : 'main');
        this.maritalStatus = answers.maritalStatus[0].id;
        this.fraOptions = {
            fraAmount: answers[userType + 'FRABenefit'],
            spousalFraAmount: answers[oppositeType + 'FRABenefit'],
            substantialYears: answers[userType + 'WorkedForNonCovered'] ? answers[userType + 'NumYearsSubstantialEarnings'] : 30
        }
        this.fraAmount = Number(this.fraOptions.fraAmount);
        this.spousalFraAmount = answers.maritalStatus[0].id === 'married' ? Number(this.fraOptions.spousalFraAmount) : Number(answers.divorcedMarriageHighestBenefit);
        this.userName = answers[userType + 'UserName']
        this.spouseName = answers[oppositeType + 'UserName']
        this.retireDate = answers[userType + 'RetireDate']
        this.pensionDetails = answers[userType + 'PensionTable'] && answers[userType + 'PensionTable'].length > 0 ? answers[userType + 'PensionTable'][0] : { isGPO: false, amount: 0 }
        this.currentSalary = Number(answers[userType + 'GrossIncome']);
        this.dob = answers[userType + 'Dob'];
        this.yearBorn = this.getYearBorn();
        this.fraYear = this.yearBorn + this.getFRAAge().years
        this.ageDifference = new Date(answers[userType + 'Dob']).getFullYear() - new Date(answers[oppositeType + 'Dob']).getFullYear()
        this.deceasedFRABenefit = answers.passedAwaySpouseBenefit;
        this.divorcedFraAmount = answers.divorcedMarriageHighestBenefit;
        this.currentYear = new Date().getFullYear()
    }

    getYearBorn() {
        return moment(this.dob, 'MM/DD/YYYY').year();
    }

    getCurrentAgeYears() {
        return Math.floor(moment().diff(moment(this.dob, 'MM/DD/YYYY'), 'years', true));
    }

    getCurrentAgeMonths() {
        return Math.floor(moment().diff(moment(this.dob, 'MM/DD/YYYY'), 'month', true));
    }

    getBenefitStartingInYear(takeYear, spousal) {
        let otherAge = takeYear - new Date(this.dob).getFullYear();
        return this.getBenefitForAge(otherAge, spousal);
    }

    getBenefitForAge(age, spousal) {
        let correctFRAAmount = spousal ? this.maritalStatus === 'divorced' ? this.divorcedFraAmount : this.spousalFraAmount : this.fraAmount;
        let lowerBenefit = this.adjustFRAForMonths(age, Number(correctFRAAmount), spousal) * 12;

        let pensionAdjusted = Math.max(this.adjustForGovernment(lowerBenefit, spousal), 0);
        let postEarningsTest = Math.max(this.earningsTestAnnual(pensionAdjusted, age + this.yearBorn), 0);

        return { amount: postEarningsTest / 12, amountYearly: pensionAdjusted, pensionReduction: lowerBenefit - pensionAdjusted, earningsTestReduction: pensionAdjusted - postEarningsTest, raw: lowerBenefit }
    }

    // This gets the cumulative amount taken, and allows us to show break even points.
    getCumTaking(startYear, amountTakingYearly) {
        let output = [];
        let cum = 0;
        let currentBase = amountTakingYearly;
        // console.log('startYear')
        // console.log(startYear)
        // console.log(amountTakingYearly)

        for (let i = 0; i < 40; i++) {
            let postEarningsTest = Math.max(this.earningsTestAnnual(currentBase, startYear + i), 0);
            cum += postEarningsTest;
            output.push({ amount: cum });

            currentBase *= 1.02;
        }
        return output;
    }

    getFRAAge() {
        if (this.yearBorn <= 1937)
            return FRAValues[1937];
        else if (this.yearBorn >= 1960)
            return FRAValues[1960];
        else
            return FRAValues[this.yearBorn];
    }

    getFRAYear() {
        let bday = moment(this.dob);
        let fraAge = this.getFRAAge();
        bday.add(fraAge.years, 'years');
        bday.add(fraAge.months, 'months');
        return bday.year();
    }

    getExemptionAmounts(yearFiled) {
        //Determine which year to use (since we won't know tax rates 20 years in the future).
        return this.getYearOrLastKnownDataFromDict(yearFiled, exemptionAmounts);
    }

    earningsTestAnnual(annualBenefit, year) {
        let exemptAmounts = this.getExemptionAmounts(year);
        if (year < this.fraYear && new Date(this.retireDate).getFullYear() > year) {
            let adjustedAGI = Number(this.currentSalary) - exemptAmounts.preFRA; //TODO check if salary is correct
            let adjustedBenefit = annualBenefit - (adjustedAGI * 0.5);
            return adjustedBenefit;
        } else if (year === this.fraYear && new Date(this.retireDate).getFullYear() > year) {
            let adjustedAGI = Math.max(Number(this.currentSalary) - exemptAmounts.yearOfFRA, 0);
            let adjustedBenefit = (annualBenefit) - Number.parseFloat(adjustedAGI / 3.0).toFixed(0);
            return adjustedBenefit;
        } else {
            return annualBenefit;
        }
    }

    adjustFRAForMonths(age, spousalAdjustedFRAAmount, spousal) {
        let fraAge = this.getFRAAge();
        let monthsFRA = 12 * fraAge.years + fraAge.months;
        let ageInMonths = age === fraAge.years ? monthsFRA : 12 * age;

        let months62 = 12 * 62;
        let months70 = 12 * 70;
        if (ageInMonths <= months62) {
            return this.getReduction(monthsFRA - months62, spousalAdjustedFRAAmount, monthsFRA, spousal);
        } else if (ageInMonths > months62 && ageInMonths < monthsFRA) {
            return this.getReduction(monthsFRA - ageInMonths, spousalAdjustedFRAAmount, monthsFRA, spousal);
        } else if (ageInMonths >= monthsFRA && ageInMonths <= months70) {
            return this.getAddition(ageInMonths - monthsFRA, spousalAdjustedFRAAmount, monthsFRA, spousal)
        } else {
            return this.getAddition(months70 - monthsFRA, spousalAdjustedFRAAmount, monthsFRA, spousal)
        }
    }

    doGPOReduction(spousalSSAmountAnnual, pensionAmt) {
        if (spousalSSAmountAnnual <= 0)
            return 0.0;
        if (pensionAmt <= 0)
            return spousalSSAmountAnnual;

        let pensionReduction = (2.0 / 3.0) * pensionAmt;
        let netSSBenefit = spousalSSAmountAnnual - pensionReduction;

        if (netSSBenefit < 0)
            return 0.0;

        return netSSBenefit;
    }

    wepAdjustment(value) {
        let aime = this.calculateAIMEFromPIA(value);
        let reduction = this.doWEPReduction(aime, this.fraOptions.substantialYears)
        return reduction;
    }

    adjustForGovernment(annualValue, spousal) {
        if (spousal) {
            if (this.pensionDetails.isGPO === true) {
                return this.doGPOReduction(annualValue, this.pensionDetails.amount);
            } else {
                return annualValue;
            }
        } else {
            return this.wepAdjustment(annualValue / 12) * 12
        }
    }

    getYearOrLastKnownDataFromDict(yearFiling, dict) {
        let yearToLookup = yearFiling - 1;
        //Determine which year to use (since we won't know tax rates 20 years in the future).
        let value;
        if (yearToLookup in dict) {
            value = dict[yearToLookup];
        } else {
            //Take latest
            let possibleYears = Object.keys(dict).sort((a, b) => Number(a) - Number(b));
            value = dict[possibleYears[possibleYears.length - 1]];
        }
        return value;
    }

    getAIMEVals() {
        return this.getYearOrLastKnownDataFromDict(2021, AIMEVals)
    }

    calculateAIMEFromPIA(PIA) {
        let adjustedBenefit = PIA;
        if (PIA < 864) {
            adjustedBenefit = 1.11 * PIA;
        } else if (PIA < 2408) {
            adjustedBenefit = 960 + 3.125 * (PIA - 864);
        } else {
            adjustedBenefit = 5785 + 6.667 * (PIA - 2408);
        }

        if (adjustedBenefit < .5 * PIA) {
            return .5 * PIA;
        }

        return adjustedBenefit
    }

    doWEPReduction(AIME, yearsContributed) {
        let wepRate = this.getAIMEVals()[0].rate;
        if (yearsContributed) {
            if (yearsContributed >= 30)
                wepRate = YOCValues[30];
            else if (yearsContributed <= 20)
                wepRate = YOCValues[20];
            else
                wepRate = YOCValues[yearsContributed];
        }

        if (AIME <= this.getAIMEVals()[0].high)
            return wepRate * AIME;

        let index = 0;
        let benefit = 0;
        do {
            if (index === 0) {
                benefit += wepRate * (AIME < this.getAIMEVals()[index].high ? AIME : this.getAIMEVals()[index].high - this.getAIMEVals()[index].low);
            } else {
                benefit += this.getAIMEVals()[index].rate * (AIME < this.getAIMEVals()[index].high ? AIME : this.getAIMEVals()[index].high - this.getAIMEVals()[index].low);
            }

            AIME -= this.getAIMEVals()[index].high;
            ++index;
        }
        while (AIME > 0);

        return benefit;
    }

    getReduction(monthsBeforeFRA, benefit, fraMonths, spousal) {
        //Spousal Reduction
        if (spousal) {
            benefit *= .5;
        }

        if (monthsBeforeFRA <= 0) {
            return benefit;
        }


        const INITIAL_DEDUCTION = (spousal ? 25 / 36 : 5 / 9) * .01;
        const ADDITIONAL_DEDUCTION = 5 / 12 * .01;
        const MAX_MONTHS_BEFORE_FRA = fraMonths - 12 * 62;

        let baseReductionMonths;
        let additionalReductionMonths = 0;

        if (monthsBeforeFRA <= 36) {
            baseReductionMonths = monthsBeforeFRA;
        } else if (monthsBeforeFRA > 36 && monthsBeforeFRA < MAX_MONTHS_BEFORE_FRA) {
            baseReductionMonths = 36;
            additionalReductionMonths = monthsBeforeFRA - 36;
        } else {
            baseReductionMonths = 36;
            additionalReductionMonths = MAX_MONTHS_BEFORE_FRA - baseReductionMonths;
        }

        let percentReduction = INITIAL_DEDUCTION * baseReductionMonths + ADDITIONAL_DEDUCTION * additionalReductionMonths;

        return benefit - (benefit * percentReduction);
    }

    getAddition(monthsAfterFRA, benefit, fraMonths, spousal) {
        //Spousal Reduction
        if (spousal) {
            return benefit * .5;
        }

        // edge case
        if (monthsAfterFRA <= 0) {
            return benefit;
        }


        const ADDITIONAL_PERCENT = 2 / 3 * .01;
        const MONTHS_70 = 70 * 12;
        const MAX_MONTHS_AFTER = MONTHS_70 - fraMonths;

        let additionalBenefitPercent = 0;
        if (monthsAfterFRA > (MAX_MONTHS_AFTER)) {
            additionalBenefitPercent = MAX_MONTHS_AFTER * ADDITIONAL_PERCENT;
        } else {
            additionalBenefitPercent = monthsAfterFRA * ADDITIONAL_PERCENT;
        }

        return benefit + (benefit * additionalBenefitPercent);
    }

    getSurvivorsBenefits(forAge) {
        if (forAge < 60) {
            return 0;
        } else if (forAge > 65) {
            return this.deceasedFRABenefit;
        } else {
            return widowValues[forAge] * this.deceasedFRABenefit;
        }
    }
}

export default SocialSecurityCalculator;